import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  AutoComplete,
  Typography,
  message,
} from 'antd';

import StrikeTeamDetailsModel from '../../models/StrikeTeamDetailsModel';
import TrackerService from '../../utils/TrackerService';
import { FormItemLayout, TailFormItemLayout } from '../../utils/Constants';
import { guessTimezoneFromAddress } from '../../utils/guessTimezoneFromAddress';

const { Text } = Typography;
const { Option } = Select;

/** ISO2 → label: EU/EEA (Eurozone + non-eurozone EU + EEA non-EU) + UK + US. */
export const ADDRESS_COUNTRY_OPTIONS = {
  IE: 'Ireland',
  GB: 'United Kingdom',
  US: 'United States',
  // Eurozone EU
  AT: 'Austria',
  BE: 'Belgium',
  CY: 'Cyprus',
  EE: 'Estonia',
  FI: 'Finland',
  FR: 'France',
  DE: 'Germany',
  GR: 'Greece',
  HR: 'Croatia',
  IT: 'Italy',
  LV: 'Latvia',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MT: 'Malta',
  NL: 'Netherlands',
  PT: 'Portugal',
  SK: 'Slovakia',
  SI: 'Slovenia',
  ES: 'Spain',
  // Non-eurozone EU + EEA non-EU (SEPA — operates in EUR)
  BG: 'Bulgaria',
  CZ: 'Czechia',
  DK: 'Denmark',
  HU: 'Hungary',
  IS: 'Iceland',
  NO: 'Norway',
  PL: 'Poland',
  RO: 'Romania',
  SE: 'Sweden',
};

const COMMON_TIMEZONES = [
  'UTC',
  'Europe/Dublin',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Amsterdam',
  'Europe/Madrid',
  'Europe/Rome',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Toronto',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Honolulu',
  'Asia/Singapore',
  'Asia/Tokyo',
];

const emailOptionalRule = {
  validator: (_, v) => {
    if (v == null || String(v).trim() === '') return Promise.resolve();
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());
    return ok ? Promise.resolve() : Promise.reject(new Error('Enter a valid email'));
  },
};

export const TeamLocationContactSettingsForm = ({ team, onUpdated, submitting = false }) => {
  const [form] = Form.useForm();
  const lastAppliedGuessRef = useRef(null);
  const userOverrodeTimezoneRef = useRef(false);
  const [suggestedTimezone, setSuggestedTimezone] = useState(null);
  const [currentTimezone, setCurrentTimezone] = useState('');

  const applyGuessIfAllowed = useCallback((addressSlice, timezoneBefore) => {
    const guess = guessTimezoneFromAddress(addressSlice || {});
    setSuggestedTimezone(guess);
    if (!guess) return;
    if (userOverrodeTimezoneRef.current) return;
    const cur = (timezoneBefore ?? form.getFieldValue('timezone') ?? '').trim();
    if (!cur || cur === lastAppliedGuessRef.current) {
      form.setFieldsValue({ timezone: guess });
      setCurrentTimezone(guess);
      lastAppliedGuessRef.current = guess;
    }
  }, [form]);

  useEffect(() => {
    if (!team) return;
    const addr = team.address && typeof team.address === 'object' ? team.address : {};
    const addressFields = {
      'address-line-one': addr['address-line-one'] ?? '',
      'address-line-two': addr['address-line-two'] ?? '',
      town: addr.town ?? '',
      county: addr.county ?? '',
      zip: addr.zip ?? '',
      country: addr.country ?? undefined,
    };
    const existingTz = (team.timezone ?? '').trim();
    const guess = guessTimezoneFromAddress(addressFields);

    userOverrodeTimezoneRef.current = !!(existingTz && guess && existingTz !== guess);
    lastAppliedGuessRef.current = existingTz && guess && existingTz === guess ? guess : null;

    form.setFieldsValue({
      contact: {
        email: team.contact?.email ?? '',
        phone: team.contact?.phone ?? '',
        mobile: team.contact?.mobile ?? '',
      },
      address: addressFields,
      timezone: existingTz || (guess && !userOverrodeTimezoneRef.current ? guess : ''),
      openingHours: {
        open: team.openingHours?.open ?? '',
        close: team.openingHours?.close ?? '',
      },
    });
    setSuggestedTimezone(guess);
    setCurrentTimezone((existingTz || (guess && !userOverrodeTimezoneRef.current ? guess : '')).trim());
    if (!existingTz && guess && !userOverrodeTimezoneRef.current) {
      lastAppliedGuessRef.current = guess;
    }
  }, [team, form]);

  const onValuesChange = (changed, allValues) => {
    if (Object.prototype.hasOwnProperty.call(changed, 'timezone')) {
      const t = (allValues.timezone != null ? String(allValues.timezone) : '').trim();
      setCurrentTimezone(t);
      const addrGuess = guessTimezoneFromAddress(allValues.address || {});
      if (!t) {
        userOverrodeTimezoneRef.current = false;
      } else if (addrGuess && t !== addrGuess) {
        userOverrodeTimezoneRef.current = true;
      } else if (addrGuess && t === addrGuess) {
        userOverrodeTimezoneRef.current = false;
        lastAppliedGuessRef.current = addrGuess;
      }
      return;
    }
    if (changed.address) {
      applyGuessIfAllowed(allValues.address, allValues.timezone);
    }
  };

  const useSuggestedTimezone = () => {
    if (!suggestedTimezone) return;
    userOverrodeTimezoneRef.current = false;
    form.setFieldsValue({ timezone: suggestedTimezone });
    setCurrentTimezone(suggestedTimezone);
    lastAppliedGuessRef.current = suggestedTimezone;
  };

  const onFinish = (values) => {
    const {
      contact, address, timezone, openingHours,
    } = values;
    if (onUpdated) {
      onUpdated({
        contact: contact || {},
        address: address || {},
        timezone: timezone ?? '',
        openingHours: openingHours || {},
      });
    }
  };

  const showSuggestionCta = suggestedTimezone
    && suggestedTimezone !== currentTimezone;

  const timezoneAutocompleteSource = useMemo(() => {
    if (suggestedTimezone && !COMMON_TIMEZONES.includes(suggestedTimezone)) {
      return [suggestedTimezone, ...COMMON_TIMEZONES];
    }
    return COMMON_TIMEZONES;
  }, [suggestedTimezone]);

  return (
    <Form
      {...FormItemLayout}
      form={form}
      name="teamLocationContactForm"
      onFinish={onFinish}
      onValuesChange={onValuesChange}
      scrollToFirstError
    >
      <Typography.Title level={5}>Contact</Typography.Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Shown to guests on your team page and in emails where contact details appear.
      </Text>
      <Form.Item
        label="Email"
        name={['contact', 'email']}
        rules={[emailOptionalRule]}
      >
        <Input type="email" placeholder="team@example.com" autoComplete="off" />
      </Form.Item>
      <Form.Item label="Phone" name={['contact', 'phone']}>
        <Input placeholder="Main phone" autoComplete="off" />
      </Form.Item>
      <Form.Item label="Mobile" name={['contact', 'mobile']}>
        <Input placeholder="Mobile / WhatsApp" autoComplete="off" />
      </Form.Item>

      <Typography.Title level={5} style={{ marginTop: 24 }}>Address</Typography.Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Venue address (optional).
      </Text>
      <Form.Item label="Address line 1" name={['address', 'address-line-one']}>
        <Input placeholder="Street and number" />
      </Form.Item>
      <Form.Item label="Address line 2" name={['address', 'address-line-two']}>
        <Input placeholder="Building, suite (optional)" />
      </Form.Item>
      <Form.Item label="Town / city" name={['address', 'town']}>
        <Input />
      </Form.Item>
      <Form.Item label="County / state" name={['address', 'county']}>
        <Input />
      </Form.Item>
      <Form.Item label="Postcode" name={['address', 'zip']}>
        <Input />
      </Form.Item>
      <Form.Item label="Country" name={['address', 'country']}>
        <Select
          allowClear
          placeholder="Select country"
          showSearch
          optionFilterProp="children"
        >
          {Object.keys(ADDRESS_COUNTRY_OPTIONS).map((code) => (
            <Option key={code} value={code}>
              {ADDRESS_COUNTRY_OPTIONS[code]}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Typography.Title level={5} style={{ marginTop: 24 }}>Timezone</Typography.Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        We suggest a timezone from your country and state or postcode when possible. You can change it
        anytime; pick &quot;Use suggested&quot; to match the address again.
      </Text>
      {suggestedTimezone && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Suggested from address:
          {' '}
          <Text code>{suggestedTimezone}</Text>
          {showSuggestionCta && (
            <>
              {' '}
              <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={useSuggestedTimezone}>
                Use suggested
              </Button>
            </>
          )}
        </Text>
      )}
      <Form.Item label="Timezone" name="timezone">
        <AutoComplete
          allowClear
          dataSource={timezoneAutocompleteSource}
          placeholder="e.g. Europe/Dublin (type any IANA ID)"
        />
      </Form.Item>

      <Form.Item {...TailFormItemLayout}>
        <Typography.Text type="secondary">
          Opening hours are optional — Always-on terminals sleep outside them
          (with an hour&apos;s grace each side). Leave blank to run 24/7.
        </Typography.Text>
      </Form.Item>
      <Form.Item
        label="Opens"
        name={['openingHours', 'open']}
        rules={[{ pattern: /^([01]?\d|2[0-3]):[0-5]\d$/, message: 'Use HH:MM (24h), e.g. 09:00' }]}
      >
        <Input placeholder="09:00" style={{ maxWidth: '120px' }} />
      </Form.Item>
      <Form.Item
        label="Closes"
        name={['openingHours', 'close']}
        rules={[{ pattern: /^([01]?\d|2[0-3]):[0-5]\d$/, message: 'Use HH:MM (24h), e.g. 22:00' }]}
      >
        <Input placeholder="22:00" style={{ maxWidth: '120px' }} />
      </Form.Item>

      <Form.Item {...TailFormItemLayout}>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting}
          style={{ width: 'auto', maxWidth: '325px' }}
        >
          Save changes
        </Button>
      </Form.Item>
    </Form>
  );
};

export const TeamLocationContactSettingsUpdate = ({ teamId, team, clientId, onLoading }) => {
  const [submitting, setSubmitting] = useState(false);

  const handleUpdate = async (payload) => {
    const model = new StrikeTeamDetailsModel({ teamId });
    onLoading && onLoading({ message: 'Saving location & contact…' });
    setSubmitting(true);
    try {
      await model.updateTeamContactAddressTimezone(payload);
      message.success('Location saved');
      try {
        const tracker = new TrackerService({ isTest: window.location.hostname === 'localhost' });
        const addrCountry = payload.address?.country || null;
        tracker.mixpanelTrack('team_address_changed', {
          distinct_id: clientId,
          clientId,
          teamId,
          teamName: team?.teamName || null,
          country: addrCountry,
          countryName: addrCountry ? (ADDRESS_COUNTRY_OPTIONS[addrCountry] || null) : null,
          town: payload.address?.town || null,
          timezone: payload.timezone || null,
          hasAddress: !!(payload.address?.['address-line-one']),
        });
        tracker.mixpanelTrack('team_contact_changed', {
          distinct_id: clientId,
          clientId,
          teamId,
          teamName: team?.teamName || null,
          email: payload.contact?.email || null,
          phone: payload.contact?.phone || null,
          mobile: payload.contact?.mobile || null,
        });
      } catch (_trackErr) { /* analytics must never surface */ }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      message.error('Could not save. Check your connection and try again.');
    } finally {
      setSubmitting(false);
      onLoading && onLoading({ message: null });
    }
  };

  return (
    <div style={{ marginTop: '12px', marginLeft: '12px' }}>
      <TeamLocationContactSettingsForm
        team={team}
        onUpdated={handleUpdate}
        submitting={submitting}
      />
    </div>
  );
};
